<template>
  <dashboard-layout title="AI Usage & Cost">
    <template #actions>
      <label class="date-field">From <input type="date" v-model="startDate" :max="endDate" /></label>
      <label class="date-field">To <input type="date" v-model="endDate" :min="startDate" /></label>
      <div class="quick-range">
        <button class="chip" @click="setRange(7)">7d</button>
        <button class="chip" @click="setRange(30)">30d</button>
        <button class="chip" @click="setRange(90)">90d</button>
      </div>
      <button class="refresh-btn" @click="loadData" :disabled="loading">{{ loading ? 'Loading…' : 'Apply' }}</button>
    </template>

    <div v-if="error" class="error-banner">
      ⚠ {{ error }}
    </div>

    <loading-state v-if="loading && !daily.length" label="Loading usage…" />

    <template v-else>
      <!-- Summary cards -->
      <stat-grid>
        <stat-card label="Total cost" :value="formatCost(totals.cost)" :sub="rangeLabel" />
        <stat-card label="Total calls" :value="totals.calls.toLocaleString('en-US')" :sub="'avg ' + formatCost(totals.calls ? totals.cost / totals.calls : 0) + ' / call'" />
        <stat-card label="Total tokens" :value="formatTokens(totals.tokens)" :sub="totals.tokens.toLocaleString('en-US')" />
        <stat-card label="Cached tokens" :value="formatTokens(totals.cached)" :sub="(totals.prompt ? Math.round((totals.cached / totals.prompt) * 100) : 0) + '% of input'" />
        <stat-card label="Cost by provider">
          <div v-if="providerTotals.length" class="provider-lines">
            <div v-for="p in providerTotals" :key="p.provider" class="provider-line">
              <span class="dot" :style="{ background: providerColor(p.provider) }"></span>
              <span class="prov-name">{{ providerLabel(p.provider) }}</span>
              <span class="prov-cost">{{ formatCost(p.cost) }}</span>
            </div>
          </div>
          <div v-else class="no-breakdown">No model breakdown in range</div>
        </stat-card>
      </stat-grid>

      <empty-state v-if="!daily.length" title="No usage data in this range.">
        Data accrues from the moment the backend started writing it — early ranges may be empty.
      </empty-state>

      <template v-else>
        <!-- Daily cost by model -->
        <data-panel title="Daily cost by model (USD)">
          <div class="chart-box"><canvas ref="trendCanvas"></canvas></div>
        </data-panel>

        <!-- Model breakdown + function breakdown + token composition -->
        <div class="dual-grid">
          <data-panel title="Cost by model">
            <div class="chart-box small"><canvas ref="modelCanvas"></canvas></div>
          </data-panel>
          <data-panel title="Cost by function">
            <div class="chart-box small"><canvas ref="fnCanvas"></canvas></div>
          </data-panel>
          <data-panel title="Token composition">
            <div class="chart-box small"><canvas ref="tokenCanvas"></canvas></div>
          </data-panel>
        </div>
      </template>

      <!-- Top spenders -->
      <data-panel flush title="Top spending users">
        <data-table :columns="topUserColumns" :rows="topUsers" row-key="userId">
          <template #cell-rank="{ index }">{{ index + 1 }}</template>
          <template #cell-user="{ row }">
            <a :href="`/user/${row.userId}`" class="user-link" @click.prevent="openUser(row.userId, $event)">{{ truncate(row.userId, 12) }}</a>
          </template>
          <template #cell-cost="{ row }">
            <span class="strong">{{ formatCost(row.totalCostUSD) }}</span>
          </template>
          <template #cell-models="{ row }">
            <div class="models-cell">
              <span v-for="m in userModels(row)" :key="m.key" class="model-chip sm" :title="`${modelDisplay(m.key)} · ${formatCost(m.costUSD)} · ${(m.calls || 0).toLocaleString('en-US')} calls`">
                <span class="dot" :style="{ background: providerColor(m.provider) }"></span>
                <span class="mc-name">{{ modelDisplay(m.key) }}</span>
                <span class="mc-cost">{{ formatCost(m.costUSD) }}</span>
              </span>
              <span v-if="!userModels(row).length" class="muted">—</span>
            </div>
          </template>
          <template #cell-calls="{ row }">{{ (row.calls || 0).toLocaleString('en-US') }}</template>
          <template #cell-tokens="{ row }">{{ formatTokens(row.totalTokens) }}</template>
          <template #cell-lastUsed="{ row }">
            <span class="date-cell">{{ formatDate(row.lastUsedAt) }}</span>
          </template>
          <template #cell-actions="{ row }">
            <button class="link-btn" @click="openDrilldown(row.userId)">Details</button>
          </template>
          <template #empty>No user data</template>
        </data-table>
      </data-panel>

      <!-- User drilldown -->
      <data-panel v-if="drilldownUser" flush class="drill-panel">
        <template #title>Calls for {{ truncate(drilldownUser, 16) }}</template>
        <template #actions>
          <button class="link-btn" @click="closeDrilldown">✕ Close</button>
        </template>
        <div v-if="drilldownByModel.length" class="model-chips">
          <span v-for="m in drilldownByModel" :key="m.key" class="model-chip">
            <span class="dot" :style="{ background: providerColor(m.provider) }"></span>
            <span class="mc-name">{{ modelDisplay(m.key) }}</span>
            <span class="mc-cost">{{ formatCost(m.costUSD) }}</span>
            <span class="mc-sub">· {{ (m.calls || 0).toLocaleString('en-US') }} calls</span>
          </span>
        </div>
        <loading-state v-if="drilldownLoading" label="Loading calls…" />
        <data-table v-else :columns="drillColumns" :rows="drilldownRows" row-key="id">
          <template #cell-date="{ row }">{{ formatDate(row.createdAt) || row.dateKey }}</template>
          <template #cell-fn="{ row }">{{ fnLabel(row.fn) }}</template>
          <template #cell-provider="{ row }">
            <span class="prov-tag" :class="row.provider">{{ row.provider }}</span>
          </template>
          <template #cell-model="{ row }">{{ row.model }}</template>
          <template #cell-prompt="{ row }">{{ (row.promptTokens || 0).toLocaleString('en-US') }}</template>
          <template #cell-completion="{ row }">{{ (row.completionTokens || 0).toLocaleString('en-US') }}</template>
          <template #cell-cached="{ row }">{{ (row.cachedTokens || 0).toLocaleString('en-US') }}</template>
          <template #cell-cost="{ row }">
            <span class="strong">{{ formatCost(row.costUSD) }}</span>
          </template>
          <template #empty>No calls in this range</template>
        </data-table>
      </data-panel>
    </template>
  </dashboard-layout>
</template>

<script>
import { ref, reactive, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Chart } from 'chart.js/auto'
import { FirebaseAiUsageService } from '../services/firebaseAiUsageService'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import DataTable from './ui/DataTable.vue'
import LoadingState from './ui/LoadingState.vue'
import EmptyState from './ui/EmptyState.vue'

const FN_LABELS = {
  'chatV2Stream': 'Chat answer',
  'chatV2Stream:intent': 'Intent detection'
}
const PALETTE = ['#6c63ff', '#4ade80', '#f8bf71', '#f87171', '#22d3ee', '#c084fc', '#fb7185', '#a3e635']

// Provider identity for the model breakdown. Colour is the base hue; models that
// share a provider are lightened per-model so they stay distinguishable in a stack.
const PROVIDER_META = {
  gemini: { label: 'Gemini', color: '#6c63ff' },
  anthropic: { label: 'Claude', color: '#d97757' },
  openai: { label: 'OpenAI', color: '#10a37f' }
}
const OTHER_COLOR = '#8b8b9e'

const topUserColumns = [
  { key: 'rank', label: '#', width: '48px' },
  { key: 'user', label: 'User', width: '140px' },
  { key: 'cost', label: 'Cost' },
  { key: 'models', label: 'Models' },
  { key: 'calls', label: 'Calls' },
  { key: 'tokens', label: 'Tokens' },
  { key: 'lastUsed', label: 'Last used' },
  { key: 'actions', label: '' }
]

const drillColumns = [
  { key: 'date', label: 'Date', cellClass: 'date-cell' },
  { key: 'fn', label: 'Function' },
  { key: 'provider', label: 'Provider' },
  { key: 'model', label: 'Model', cellClass: 'model-cell' },
  { key: 'prompt', label: 'Prompt' },
  { key: 'completion', label: 'Completion' },
  { key: 'cached', label: 'Cached' },
  { key: 'cost', label: 'Cost' }
]

function ymdIstanbul(d) {
  return d.toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' })
}

// Lighten a hex colour toward white by `percent` (0–100).
function shade(hex, percent) {
  const h = hex.replace('#', '')
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  const mix = c => Math.round(c + (255 - c) * (percent / 100))
  const to2 = v => v.toString(16).padStart(2, '0')
  return `#${to2(mix(r))}${to2(mix(g))}${to2(mix(b))}`
}

export default {
  name: 'AiUsageDashboard',
  components: { DashboardLayout, StatGrid, StatCard, DataPanel, DataTable, LoadingState, EmptyState },
  setup() {
    const router = useRouter()
    const service = new FirebaseAiUsageService()

    const today = new Date()
    const endDate = ref(ymdIstanbul(today))
    const startDate = ref(ymdIstanbul(new Date(today.getTime() - 29 * 86400000)))

    const loading = ref(false)
    const error = ref('')
    const daily = ref([])
    const topUsers = ref([])

    const drilldownUser = ref('')
    const drilldownRows = ref([])
    const drilldownByModel = ref([])
    const drilldownLoading = ref(false)

    const trendCanvas = ref(null)
    const modelCanvas = ref(null)
    const fnCanvas = ref(null)
    const tokenCanvas = ref(null)
    let trendChart = null
    let modelChart = null
    let fnChart = null
    let tokenChart = null

    const totals = reactive({ cost: 0, calls: 0, tokens: 0, prompt: 0, completion: 0, cached: 0 })

    const recomputeTotals = () => {
      const t = { cost: 0, calls: 0, tokens: 0, prompt: 0, completion: 0, cached: 0 }
      daily.value.forEach(d => {
        t.cost += d.costUSD || 0
        t.calls += d.calls || 0
        t.tokens += d.totalTokens || 0
        t.prompt += d.promptTokens || 0
        t.completion += d.completionTokens || 0
        t.cached += d.cachedTokens || 0
      })
      Object.assign(totals, t)
    }

    const fnAggregate = () => {
      const agg = {}
      daily.value.forEach(d => {
        const byFn = d.byFn || {}
        Object.keys(byFn).forEach(fn => {
          if (!agg[fn]) agg[fn] = 0
          agg[fn] += byFn[fn].costUSD || 0
        })
      })
      return Object.entries(agg).sort((a, b) => b[1] - a[1])
    }

    // Period cost totalled per provider (gemini / anthropic / openai / …), for the
    // summary card. Reads aiUsageDaily.byModel across the loaded range.
    const providerTotals = computed(() => {
      const agg = {}
      daily.value.forEach(d => {
        const bm = d.byModel || {}
        Object.keys(bm).forEach(k => {
          const e = bm[k]
          const p = e.provider || 'other'
          if (!agg[p]) agg[p] = { provider: p, cost: 0, calls: 0, tokens: 0 }
          agg[p].cost += e.costUSD || 0
          agg[p].calls += e.calls || 0
          agg[p].tokens += e.totalTokens || 0
        })
      })
      return Object.values(agg).sort((a, b) => b.cost - a.cost)
    })

    // Aggregate byModel across the loaded range: keys ordered by total cost,
    // per-model totals, and a colour per model (provider hue, shaded per model
    // within a provider). Shared by the stacked daily chart and the doughnut.
    const modelAggregate = () => {
      const totals = {}
      const providerOf = {}
      daily.value.forEach(d => {
        const bm = d.byModel || {}
        Object.keys(bm).forEach(k => {
          totals[k] = (totals[k] || 0) + (bm[k].costUSD || 0)
          if (!providerOf[k]) providerOf[k] = bm[k].provider || 'other'
        })
      })
      const keys = Object.keys(totals).sort((a, b) => totals[b] - totals[a])
      const perProviderCount = {}
      const colorFor = {}
      keys.forEach(k => {
        const p = providerOf[k]
        const idx = perProviderCount[p] || 0
        perProviderCount[p] = idx + 1
        const base = providerColor(p)
        colorFor[k] = idx === 0 ? base : shade(base, Math.min(idx * 20, 60))
      })
      return { keys, totals, colorFor }
    }

    const setRange = days => {
      const now = new Date()
      endDate.value = ymdIstanbul(now)
      startDate.value = ymdIstanbul(new Date(now.getTime() - (days - 1) * 86400000))
      loadData()
    }

    const destroyCharts = () => {
      [trendChart, modelChart, fnChart, tokenChart].forEach(c => c && c.destroy())
      trendChart = modelChart = fnChart = tokenChart = null
    }

    const baseScales = {
      x: { grid: { display: false }, ticks: { color: '#8b8b8b', font: { size: 11 } } },
      y: { beginAtZero: true, grid: { color: '#2a2a2a' }, ticks: { color: '#8b8b8b', font: { size: 11 } } }
    }
    const legendDark = { labels: { color: '#cfcfe1', padding: 12, font: { size: 12 }, usePointStyle: true } }

    const renderCharts = () => {
      destroyCharts()
      if (!daily.value.length) return

      // 1) daily cost by model (stacked bar). Falls back to a single total-cost
      //    line for ranges that predate byModel (should be none — backfilled).
      if (trendCanvas.value) {
        const tooltipDark = {
          backgroundColor: '#1a1b2e', titleColor: '#fff', bodyColor: '#a9a9b3',
          borderColor: '#2c2d42', borderWidth: 1, padding: 12
        }
        const { keys, colorFor } = modelAggregate()
        if (keys.length) {
          const datasets = keys.map(k => ({
            label: modelDisplay(k),
            data: daily.value.map(d => +((d.byModel && d.byModel[k] ? d.byModel[k].costUSD : 0)).toFixed(6)),
            backgroundColor: colorFor[k],
            borderColor: '#1a1b2e',
            borderWidth: 1
          }))
          trendChart = new Chart(trendCanvas.value.getContext('2d'), {
            type: 'bar',
            data: { labels: daily.value.map(d => d.date), datasets },
            options: {
              responsive: true, maintainAspectRatio: false,
              plugins: {
                legend: { position: 'bottom', ...legendDark },
                tooltip: {
                  ...tooltipDark,
                  callbacks: {
                    label: c => ` ${c.dataset.label}: $${(c.parsed.y || 0).toFixed(4)}`,
                    footer: cs => 'Total: $' + cs.reduce((s, c) => s + (c.parsed.y || 0), 0).toFixed(4)
                  }
                }
              },
              scales: {
                x: { ...baseScales.x, stacked: true },
                y: { ...baseScales.y, stacked: true, ticks: { ...baseScales.y.ticks, callback: v => '$' + v } }
              }
            }
          })
        } else {
          trendChart = new Chart(trendCanvas.value.getContext('2d'), {
            type: 'line',
            data: {
              labels: daily.value.map(d => d.date),
              datasets: [{
                label: 'Cost (USD)',
                data: daily.value.map(d => +(d.costUSD || 0).toFixed(6)),
                borderColor: '#6c63ff',
                backgroundColor: 'rgba(108,99,255,0.15)',
                borderWidth: 2.5, fill: true, tension: 0.25,
                pointRadius: 3, pointHoverRadius: 6, pointBackgroundColor: '#6c63ff'
              }]
            },
            options: {
              responsive: true, maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: { ...tooltipDark, callbacks: { label: c => ` $${(c.parsed.y || 0).toFixed(4)}` } }
              },
              scales: {
                ...baseScales,
                y: { ...baseScales.y, ticks: { ...baseScales.y.ticks, callback: v => '$' + v } }
              }
            }
          })
        }
      }

      // 2) cost by model (doughnut) — same provider colours as the stacked chart
      if (modelCanvas.value) {
        const { keys, totals: modelTotals, colorFor } = modelAggregate()
        if (keys.length) {
          modelChart = new Chart(modelCanvas.value.getContext('2d'), {
            type: 'doughnut',
            data: {
              labels: keys.map(k => modelDisplay(k)),
              datasets: [{ data: keys.map(k => +modelTotals[k].toFixed(6)), backgroundColor: keys.map(k => colorFor[k]), borderColor: '#1a1b2e', borderWidth: 2 }]
            },
            options: {
              responsive: true, maintainAspectRatio: false, cutout: '60%',
              plugins: {
                legend: { position: 'bottom', ...legendDark },
                tooltip: { callbacks: { label: c => ` ${c.label}: $${(c.parsed || 0).toFixed(4)}` } }
              }
            }
          })
        }
      }

      // 3) cost by function (doughnut)
      if (fnCanvas.value) {
        const entries = fnAggregate()
        fnChart = new Chart(fnCanvas.value.getContext('2d'), {
          type: 'doughnut',
          data: {
            labels: entries.map(([fn]) => fnLabel(fn)),
            datasets: [{ data: entries.map(([, v]) => +v.toFixed(6)), backgroundColor: PALETTE, borderColor: '#1a1b2e', borderWidth: 2 }]
          },
          options: {
            responsive: true, maintainAspectRatio: false, cutout: '60%',
            plugins: {
              legend: { position: 'bottom', ...legendDark },
              tooltip: { callbacks: { label: c => ` ${c.label}: $${(c.parsed || 0).toFixed(4)}` } }
            }
          }
        })
      }

      // 4) token composition (doughnut) — prompt includes cached, so split it out
      if (tokenCanvas.value) {
        const uncachedPrompt = Math.max(0, totals.prompt - totals.cached)
        tokenChart = new Chart(tokenCanvas.value.getContext('2d'), {
          type: 'doughnut',
          data: {
            labels: ['Prompt (uncached)', 'Cached', 'Completion'],
            datasets: [{ data: [uncachedPrompt, totals.cached, totals.completion], backgroundColor: ['#6c63ff', '#22d3ee', '#4ade80'], borderColor: '#1a1b2e', borderWidth: 2 }]
          },
          options: {
            responsive: true, maintainAspectRatio: false, cutout: '60%',
            plugins: {
              legend: { position: 'bottom', ...legendDark },
              tooltip: { callbacks: { label: c => ` ${c.label}: ${(c.parsed || 0).toLocaleString('en-US')}` } }
            }
          }
        })
      }
    }

    const loadData = async () => {
      loading.value = true
      error.value = ''
      try {
        const res = await service.getUsage({ startDate: startDate.value, endDate: endDate.value, topUsers: 20 })
        daily.value = [...res.daily].sort((a, b) => (a.date || '').localeCompare(b.date || ''))
        topUsers.value = res.topUsers
        recomputeTotals()
        // Charts are redrawn by the flush:'post' watch on `daily` below, once the
        // DOM (including the panels that first mount when data arrives) is patched.
      } catch (e) {
        console.error(e)
        const code = e && e.code ? e.code : ''
        if (code === 'permission-denied') {
          error.value = 'Firestore denied the read (not signed in). Reload the page and try again.'
        } else if (code === 'failed-precondition') {
          error.value = (e && e.message) ? e.message : 'A Firestore index is required for this query.'
        } else {
          error.value = (e && e.message) ? e.message : 'Failed to load AI usage.'
        }
        daily.value = []
        topUsers.value = []
      } finally {
        loading.value = false
      }
    }

    const openDrilldown = async uid => {
      drilldownUser.value = uid
      drilldownRows.value = []
      // Aggregated per-model breakdown for this user (aiUsageByUser.{uid}.byModel).
      const u = topUsers.value.find(x => x.userId === uid)
      drilldownByModel.value = u && u.byModel
        ? Object.keys(u.byModel).map(k => ({ key: k, ...u.byModel[k] })).sort((a, b) => b.costUSD - a.costUSD)
        : []
      drilldownLoading.value = true
      try {
        const res = await service.getUsage({ startDate: startDate.value, endDate: endDate.value, userId: uid })
        drilldownRows.value = (res.userDrilldown || []).slice().sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt))
      } catch (e) {
        console.error(e)
        error.value = (e && e.message) ? e.message : 'Failed to load user calls.'
      } finally {
        drilldownLoading.value = false
      }
    }
    const closeDrilldown = () => { drilldownUser.value = ''; drilldownRows.value = []; drilldownByModel.value = [] }

    const rangeLabel = computed(() => `${startDate.value} → ${endDate.value}`)

    const fnLabel = fn => FN_LABELS[fn] || fn || '—'
    const providerLabel = p => (PROVIDER_META[p] && PROVIDER_META[p].label) || (p ? p.charAt(0).toUpperCase() + p.slice(1) : 'Other')
    const providerColor = p => (PROVIDER_META[p] && PROVIDER_META[p].color) || OTHER_COLOR
    // Firestore stored keys with dots as underscores (gpt-4_1 → gpt-4.1); undo for display.
    const modelDisplay = k => k ? k.replace(/_/g, '.') : '—'
    // Per-user model breakdown (aiUsageByUser.{uid}.byModel), highest cost first.
    const userModels = u => (u && u.byModel)
      ? Object.keys(u.byModel).map(k => ({ key: k, ...u.byModel[k] })).sort((a, b) => b.costUSD - a.costUSD)
      : []
    const truncate = (t, max) => !t ? '-' : (t.length > max ? t.slice(0, max) + '…' : t)
    const formatCost = n => '$' + (n || 0).toFixed(n && n < 0.01 ? 6 : 4)
    const formatTokens = n => {
      const v = n || 0
      if (v < 1000) return String(v)
      if (v < 1e6) return (v / 1e3).toFixed(1) + 'K'
      return (v / 1e6).toFixed(2) + 'M'
    }
    const toMs = v => {
      if (!v) return 0
      if (typeof v === 'object' && v._seconds) return v._seconds * 1000
      if (typeof v === 'object' && v.seconds) return v.seconds * 1000
      const t = new Date(v).getTime()
      return isNaN(t) ? 0 : t
    }
    const formatDate = v => {
      const ms = toMs(v)
      if (!ms) return ''
      return new Date(ms).toLocaleString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    }
    const openUser = (uid, event) => {
      if (event.metaKey || event.ctrlKey) window.open(`/user/${uid}`, '_blank')
      else router.push(`/user/${uid}`)
    }

    // Redraw whenever the data changes, AFTER the DOM (incl. the chart panels that
    // first mount when data arrives) is patched — guarantees canvas refs are ready.
    watch(daily, () => renderCharts(), { flush: 'post' })

    onMounted(loadData)
    onBeforeUnmount(destroyCharts)

    return {
      startDate, endDate, loading, error, daily, topUsers, totals,
      drilldownUser, drilldownRows, drilldownByModel, drilldownLoading,
      trendCanvas, modelCanvas, fnCanvas, tokenCanvas,
      topUserColumns, drillColumns,
      providerTotals, providerLabel, providerColor, modelDisplay, userModels,
      rangeLabel, setRange, loadData, openDrilldown, closeDrilldown,
      fnLabel, truncate, formatCost, formatTokens, formatDate, openUser
    }
  }
}
</script>

<style scoped>
/* Date range + quick-range controls in the header actions slot */
.date-field {
  font-size: 12px;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 6px;
}
.date-field input {
  padding: 7px 10px;
  background: var(--field-bg);
  border: 1px solid var(--panel-border);
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  font-size: 13px;
  color-scheme: dark;
}
.quick-range { display: flex; gap: 4px; }
.refresh-btn {
  padding: 8px 14px;
  background: var(--primary-color);
  border: none;
  border-radius: var(--radius-sm);
  color: #fff;
  font-size: 13px;
  cursor: pointer;
}
.refresh-btn:disabled { opacity: 0.5; cursor: default; }
.chip {
  padding: 6px 10px;
  background: var(--field-bg);
  border: 1px solid var(--panel-border);
  border-radius: 16px;
  color: var(--text-secondary);
  font-size: 12px;
  cursor: pointer;
}
.chip:hover { color: var(--text-primary); }

.error-banner {
  background: rgba(248, 191, 113, 0.12);
  border: 1px solid rgba(248, 191, 113, 0.35);
  color: #f8bf71;
  border-radius: var(--radius-sm);
  padding: 10px 14px;
  font-size: 13px;
}

/* Provider totals card */
.provider-lines { display: flex; flex-direction: column; gap: 6px; }
.provider-line { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 400; }
.provider-line .dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.prov-name { color: var(--text-primary); flex: 1; }
.prov-cost { color: var(--text-primary); font-weight: 600; }
.no-breakdown { font-size: 12px; font-weight: 400; color: var(--text-secondary); }

/* Per-user model chips (drilldown) */
.model-chips { display: flex; flex-wrap: wrap; gap: 8px; padding: 14px 16px 0; }
.model-chip {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 5px 10px; border-radius: 16px; font-size: 12px;
  background: var(--field-bg); border: 1px solid var(--panel-border);
  color: var(--text-primary);
}
.model-chip .dot { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }
.model-chip .mc-name { font-weight: 500; }
.model-chip .mc-cost { font-weight: 600; }
.model-chip .mc-sub { color: var(--text-secondary); }
.model-chip.sm { padding: 3px 8px; font-size: 11px; gap: 5px; }
.model-chip.sm .mc-cost { color: var(--text-secondary); font-weight: 600; }

/* Models column in the top-users table */
.models-cell { display: flex; flex-wrap: wrap; gap: 5px; max-width: 320px; }

/* Chart sizing */
.dual-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 12px;
}
.chart-box { height: 320px; position: relative; }
.chart-box.small { height: 280px; }

/* Table cell helpers (used inside DataTable cell slots) */
.strong { font-weight: 600; }
.date-cell { white-space: nowrap; }
.model-cell { font-size: 12px; color: var(--text-secondary); }
.prov-tag {
  font-size: 11px; padding: 2px 8px; border-radius: 10px;
  background: rgba(108, 99, 255, 0.15); border: 1px solid rgba(108, 99, 255, 0.3); color: var(--text-primary);
}
.prov-tag.openai { background: rgba(74, 222, 128, 0.12); border-color: rgba(74, 222, 128, 0.3); }
.prov-tag.gemini { background: rgba(108, 99, 255, 0.15); border-color: rgba(108, 99, 255, 0.3); }
.user-link { color: var(--primary-color); text-decoration: none; }
.user-link:hover { text-decoration: underline; }
.link-btn {
  background: none; border: none; color: var(--primary-color); cursor: pointer; font-size: 12.5px; padding: 0;
}
.link-btn:hover { text-decoration: underline; }
.muted { color: var(--text-secondary); font-size: 13px; }
</style>
